import {useEffect, useMemo, useState} from 'react';
import {Box, Typography, useMediaQuery} from '@mui/material';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import {ReactComponent as SuccessIcon} from '@icons/successfully.svg';
import {ReactComponent as ArrowRightIcon} from '@icons/arrow-line-right.svg';
import {ReactComponent as LocationIcon} from '@icons/location.svg';
import {ReactComponent as ProfileIcon} from '@icons/profile.svg';
import {ReactComponent as TicketIcon} from '@icons/sidebar/ticket.svg';
import {ReactComponent as MoneyIcon} from '@icons/sidebar/money.svg';
import {ReactComponent as CalendarIcon} from '@icons/calendar.svg';
import {ReactComponent as ClockIcon} from '@icons/clock.svg';
import {ReactComponent as PaymentIcon} from '@icons/payment-method.svg';
import {
  BODY1,
  BODY2,
  CAPTION,
  EVENT_ID,
  H6,
  IMAGE_PREVIEW_ALT,
  MERIDIEM_HOUR_FORMAT,
  MM_DD_YYYY_SLASH,
  OUTLINE,
  SUBTITLE1,
  SUBTITLE2,
  TEXT,
} from '@/utils/constants';
import Button from '@/shared/components/Button';
import LotItem from '@/components/User/LotItem';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {PAYMENT_METHOD, ROUTER_URL} from '@/utils/enums';
import ProgressBar from '@/shared/components/ProgressBar';
import {homeStyle} from './Home.styles';
import Modal from '@/shared/components/Modal';
import AddTableTicket from '@/components/Organization/TablesAndTickets/AddTableTicket';
import NeedBeLoggedModal from '@/components/User/NeedBeLogged';
import {useSelector} from 'react-redux';
import {getUserInfo, getUserSelector} from '@/store/user';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import MakeDonationModal, {donationOptions} from '@/components/User/MakeDonationModal';
import {getDonationListByEvent, userMakeDonation} from '@/store/user-donation/actions';
import {getDefaultRequestBody, getErrorMessage, getImagePreview} from '@/utils/helpers';
import {toast} from 'react-toastify';
import {getCurrentEventSelector, getEventBySlug, getSponsorCategoriesSelector} from '@/store/organization-event';
import dayjs from 'dayjs';
import {clearPrivateTicketsTiers, getTicketsList, getTicketsTiers} from '@/store/organization-tables';
import {useAuctionTimer} from '@/utils/hooks';
import {currencyFormatter} from '@/utils/helpers/currency';
import {userAPI} from '@/core/api';
import Loader from '@/shared/components/Loader';
import PaymentOptions from '../Profile/General/PaymentOptions';
import {OrganizationAttendeesService} from '@/utils/services';
import {profileContactList} from '../Profile/General/ProfileGeneral.style';
import TestImage from '@/assets/images/test-table-image.png';
import {EventSponsor} from '@/pages/Organization/EventSettings/formConfiguration';
import { useCardAttachment, WaitResult } from '@/utils/hooks/useCardAttachment';
import axios from 'axios';


const getCardErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if ((error.response?.status ?? 0) >= 500) {
      return 'Something went wrong on our side. Please try again later.';
    }
    return getErrorMessage(error);
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again later.';
};

const Home = ({slugFromProps}: {slugFromProps?: string}) => {
  const {slug: slugFromUrl} = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const slug = slugFromUrl && slugFromUrl !== 'undefined' ? slugFromUrl : slugFromProps;

  const user = useSelector(getUserSelector);
  const event = useAppSelector(getCurrentEventSelector);
  const sponsorCategories = useAppSelector(getSponsorCategoriesSelector);

  const { isStarting, isWaiting, waitForAttachment, startAttachment } = useCardAttachment(event?.id, location.pathname);

  const [isShowAddTicketModal, setIsShowAddTicketModal] = useState<boolean>(false);
  const [isNeedBeLoggedModalOpen, setIsNeedBeLoggedModalOpen] = useState(false);
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isSuccessModalOpen2, setIsSuccessModalOpen2] = useState(false);
  const [isAttentionModalOpen, setIsAttentionModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState(donationOptions[0]);
  const [error, setError] = useState('');
  const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
  const [paymentOption, setPaymentOption] = useState(
    user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
  );

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const {hasEnded} = useAuctionTimer(event?.endAuctionData);

  const UNCATEGORIZED = 'Uncategorized';

  const progressValue = event?.isGoalAmount
    ? event?.goalAmount > (event?.raisedAmount ?? 0)
      ? ((event?.raisedAmount ?? 0) / event?.goalAmount) * 100
      : 100
    : (event?.raisedAmount ?? 0) > 0
    ? 100
    : 0;

  useEffect(() => {
    if (user?.paymentMethod) setPaymentOption(user.paymentMethod);
  }, [user?.paymentMethod]);

  // NEW: тост обробки картки
  useEffect(() => {
    if (!isWaiting) return undefined;

    const toastId = toast.loading('We are processing your card. This may take a few seconds...');

    return () => {
      toast.dismiss(toastId);
    };
  }, [isWaiting]);

  const handleAttentionModalChange = (title: string, description: string) => {
    if (title && description) {
      setModalTitle(title);
      setModalDescription(description);
    }
    setIsAttentionModalOpen(!isAttentionModalOpen);
  };

  const handleAddTicketModalShow = async () => {
    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
    } else if (!event?.tableInfos?.length) {
      handleAttentionModalChange(
        'Table Tiers Coming Soon',
        'The organizer hasn`t set up the table tiers yet. Please check back later'
      );
      // eslint-disable-next-line no-useless-return
      return;
    } else {
      await getTableData();
      isLaptop ? setIsShowAddTicketModal(true) : navigate(ROUTER_URL.USER_PURCHASE_TICKETS);
    }
  };

  const handleNextStep = () => setPaymentStep(2);
  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
    setPaymentStep(1);
  };

  // ---------- Payment: старе (не-карткові способи) ----------
  const addPaymentOption = async () => {
    if (!user?.bidderId || !user?.id) return;
    try {
      const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
      if (res.status === 204) {
        await dispatch(getUserInfo(user?.id)).unwrap();
        toast.success('Payment method added successfully!');
      }

      handleClosePaymentModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ---------- Payment: Stripe-флоу для картки (NEW) ----------
  const syncUser = async () => {
    if (user?.id) await dispatch(getUserInfo(user.id)).unwrap();
  };

  const handleWaitResult = async (result: WaitResult) => {
    if (result === 'attached') {
      await syncUser();
      toast.success('Payment method added successfully!', {toastId: 'card-attached'});
    }
    if (result === 'timeout') {
      toast.error('Card verification is taking longer than expected. Please refresh the page in a moment.', {
        toastId: 'card-timeout',
        autoClose: 8000,
      });
    }
  };

  const addCardViaStripe = async () => {
    try {
      const result = await startAttachment();
      if (result === 'redirect') return;

      handleClosePaymentModal();

      if (result === 'attached') {
        await syncUser();
        toast.success('Payment method is already attached');
        return;
      }

      // processing
      await handleWaitResult(await waitForAttachment());
    } catch (error) {
      handleClosePaymentModal();
      toast.error(getCardErrorMessage(error), {toastId: 'card-setup-error', autoClose: 8000});
    }
  };

  const handleConfirmPayment = () =>
    paymentOption === PAYMENT_METHOD.CREDIT_CARD ? addCardViaStripe() : addPaymentOption();

  const handleMakeDonationModalShow = () => {
    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
    } else if (!event?.isFundTheMission) {
      handleAttentionModalChange(
        'Thank You for Wanting to Help!',
        'The organizer has paused donation for this auction.'
      );
    } else if (!user.paymentMethod) {
      setIsShowPaymentModal(true);
    } else {
      setIsDonationModalOpen(true);
      setSelectedOption(donationOptions[0]);
    }
  };

  const groupSponsorsByCategory = (sponsors: EventSponsor[] | null) =>
    (sponsors || []).reduce<Record<string, EventSponsor[]>>((acc, sponsor) => {
      const key = sponsor.category?.name.toLowerCase() || UNCATEGORIZED;
      if (!acc[key]) acc[key] = [];
      acc[key].push(sponsor);
      return acc;
    }, {});

  const groupedSponsors = groupSponsorsByCategory(event?.sponsors || []);

  const sortedCategoryNames = useMemo(() => {
    const knownOrder = new Map((sponsorCategories || []).map((c) => [c.name, c.sortOrder]));
    return Object.keys(groupedSponsors).sort(
      (a, b) => (knownOrder.get(a) ?? Infinity) - (knownOrder.get(b) ?? Infinity)
    );
  }, [groupedSponsors, sponsorCategories]);

  const handleAddDonation = async () => {
    if (!user || !event) return;

    const numericAmount = parseFloat(selectedOption.replace(',', '.'));

    if (!selectedOption || Number.isNaN(numericAmount) || numericAmount <= 0) {
      toast.error('Donation amount should be greater than 0');
      return;
    }

    const reqBody = {
      amount: numericAmount,
      eventId: event?.id,
    };

    try {
      await dispatch(userMakeDonation({reqBody, apiInstance: userAPI})).unwrap();
      await dispatch(getDonationListByEvent({reqBody: event?.id, apiInstance: userAPI})).unwrap();
      setIsSuccessModalOpen(true);
      setIsDonationModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setError('');
    }
  };

  useEffect(() => {
    if (selectedOption || Number(selectedOption) !== 0) setError('');
  }, [selectedOption]);

  const getAuctionListData = async () => {
    if (!slug) {
      setIsLoading(false);
      return;
    }

    try {
      await dispatch(getEventBySlug(slug)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const getTableData = async () => {
    if (!event?.id) return;

    try {
      const reqBody = getDefaultRequestBody(event.id, EVENT_ID);

      await Promise.all([
        dispatch(getTicketsList({reqBody, apiInstance: userAPI})).unwrap(),
        dispatch(getTicketsTiers({reqBody: event.id, apiInstance: userAPI})).unwrap(),
      ]);
      dispatch(clearPrivateTicketsTiers());
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const onSuccess = () => {
    setIsSuccessModalOpen2(true);
  };

  useEffect(() => {
    setIsLoading(true);
    getAuctionListData();
  }, [slug]);

  if (isLoading) {
    return (
      <Box sx={homeStyle.wrapper}>
        <Loader parentClassName={homeStyle.loader} size={50} />
      </Box>
    );
  }

  return (
    <Box sx={homeStyle.wrapper}>
      <Box sx={homeStyle.imageWrapper}>
        <Box sx={homeStyle.image}>
          <img src={event?.image} style={{width: '100%'}} alt="eventImg" />
        </Box>
        <Box sx={homeStyle.sectionWrapper}>
          <Box>
            <Typography variant={H6} sx={homeStyle.title}>
              {event?.name}
            </Typography>
            <Box sx={{marginBottom: '8px'}}>
              <Typography variant={BODY2} sx={homeStyle.address}>
                <LocationIcon />
                {event?.location}
              </Typography>
              <Typography variant={BODY2} sx={homeStyle.address}>
                <ProfileIcon />
                {` Attendees: ${event?.checkedIn} of ${event?.allGuests}`}
              </Typography>
            </Box>
            <Typography variant={BODY2} sx={homeStyle.data}>
              Start:
              <Typography sx={homeStyle.time}>
                <Typography variant={BODY2} sx={homeStyle.alignWrapper}>
                  <CalendarIcon />
                  {dayjs(event?.startData).format(MM_DD_YYYY_SLASH)}
                </Typography>
                <Typography variant={BODY2} sx={homeStyle.alignWrapper}>
                  <ClockIcon /> {dayjs(event?.startData).format(MERIDIEM_HOUR_FORMAT)}
                </Typography>
              </Typography>
            </Typography>
            <Typography variant={BODY2} sx={homeStyle.data}>
              End:
              <Typography sx={homeStyle.time}>
                <Typography variant={BODY2} sx={homeStyle.alignWrapper}>
                  <CalendarIcon />
                  {dayjs(event?.endData).format(MM_DD_YYYY_SLASH)}
                </Typography>
                <Typography variant={BODY2} sx={homeStyle.alignWrapper}>
                  <ClockIcon />
                  {dayjs(event?.endData).format(MERIDIEM_HOUR_FORMAT)}
                </Typography>
              </Typography>
            </Typography>
          </Box>

          <Box sx={homeStyle.progressWrap}>
            <Box sx={homeStyle.progressValue}>
              <Typography variant={BODY1} sx={homeStyle.progressTargetAmount}>
                <Typography sx={homeStyle.progressTargetGoal} variant={CAPTION}>
                  Raised
                </Typography>
                {`${currencyFormatter.format(event?.raisedAmount || 0)}`}
              </Typography>
              {event?.isGoalAmount && (
                <Typography sx={homeStyle.progressTargetGoal} variant={SUBTITLE2}>
                  <Typography sx={homeStyle.progressTargetGoal} variant={CAPTION}>
                    Goal amount
                  </Typography>
                  {`${currencyFormatter.format(event?.goalAmount)}`}
                </Typography>
              )}
            </Box>
            <ProgressBar value={progressValue} />
          </Box>

          <Box sx={homeStyle.buttonWrapper}>
            <Button
              startIcon={
                <Box sx={homeStyle.moneyIconWrapper}>
                  <MoneyIcon />
                </Box>
              }
              variant={OUTLINE}
              title="Make Donation"
              onClick={handleMakeDonationModalShow}
              disableRipple
            />
            <Button
              startIcon={
                <Box sx={homeStyle.iconWrapper}>
                  <TicketIcon />
                </Box>
              }
              title="Buy Tickets"
              onClick={handleAddTicketModalShow}
              disableRipple
            />
          </Box>
        </Box>
      </Box>
      {event?.description && (
        <Box sx={homeStyle.textWrapper}>
          <Typography variant={SUBTITLE1} sx={homeStyle.title}>
            About The Event
          </Typography>
          <Typography sx={homeStyle.description} variant={BODY1}>
            {event?.description}
          </Typography>
        </Box>
      )}

      {!!event?.sponsors?.length && (
        <Box sx={homeStyle.textWrapper}>
          <Typography variant={SUBTITLE1} sx={homeStyle.title}>
            Event Sponsors
          </Typography>
          {sortedCategoryNames.map((categoryName) => (
            <Box key={categoryName}>
              <Typography variant={SUBTITLE2} sx={homeStyle.sponsorTitle}>
                {categoryName}
              </Typography>
              <Box sx={homeStyle.sponsorList}>
                {groupedSponsors[categoryName].map((item: EventSponsor) => (
                  <Box key={item.id} sx={homeStyle.sponsor}>
                    <Box sx={homeStyle.sponsorImage}>
                      <img src={getImagePreview(item.logo || TestImage)} alt={item.name || IMAGE_PREVIEW_ALT} />
                    </Box>
                    <Box sx={homeStyle.sponsorName} title={item.name}>
                      {item.name}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {event?.popularItems && event?.popularItems?.length > 0 && (
        <Box>
          <Box sx={homeStyle.popularWrapper}>
            <Typography variant={SUBTITLE1}>Popular Auction Items</Typography>
            <Button
              title="View All Items"
              endIcon={<ArrowRightIcon />}
              variant={TEXT}
              onClick={() => navigate(ROUTER_URL.USER_ALL_ITEMS)}
            />
          </Box>
          <Box sx={homeStyle.lotList}>
            {event?.popularItems &&
              event?.popularItems.map((item) => {
                return (
                  <LotItem
                    key={item.id}
                    data={item}
                    auctionType="AllItems"
                    getListData={getAuctionListData}
                    hasEnded={hasEnded}
                  />
                );
              })}
          </Box>
        </Box>
      )}
      <Modal
        onClose={() => setIsShowAddTicketModal(false)}
        open={isShowAddTicketModal}
        sx={homeStyle.modalTickets}
        title="Buy Tickets"
      >
        <AddTableTicket onCloseModal={() => setIsShowAddTicketModal(false)} onSuccess={onSuccess} />
      </Modal>
      <Modal
        onClose={() => setIsNeedBeLoggedModalOpen(false)}
        open={isNeedBeLoggedModalOpen}
        sx={homeStyle.modalLogin}
        mainIcon={<DangerIcon />}
      >
        <NeedBeLoggedModal handleClose={() => setIsNeedBeLoggedModalOpen(false)} />
      </Modal>
      <Modal
        isShowSaveButton
        mainButtonTitle={`Donate $${selectedOption}`}
        isSaveButtonDisable={!selectedOption}
        isShowCloseButton
        closeButtonTitle="Close"
        onClose={() => {
          setIsDonationModalOpen(false);
          setError('');
        }}
        open={isDonationModalOpen}
        sx={homeStyle.modalBid}
        onSave={handleAddDonation}
      >
        <MakeDonationModal selectedOption={selectedOption} setSelectedOption={setSelectedOption} error={error} />
      </Modal>
      <Modal
        open={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
          setSelectedOption(donationOptions[0]);
        }}
        title="Thank You for Your Donation"
        description={`Your donation of $${selectedOption} successfully processed.`}
        sx={homeStyle.modalLogin}
        closeButtonTitle="Done"
        mainIcon={<SuccessIcon />}
      />
      <Modal
        open={isSuccessModalOpen2}
        onClose={() => setIsSuccessModalOpen2(false)}
        title="Thank You For Your Support!"
        description="Your tickets have been successfully purchased. A receipt and additional information will be delivered by email."
        sx={homeStyle.modalLogin}
        mainButtonTitle="Done"
        onSave={() => setIsSuccessModalOpen2(false)}
        isShowSaveButton
        mainIcon={<SuccessIcon />}
      />
      <Modal
        open={isAttentionModalOpen}
        onClose={() => handleAttentionModalChange('', '')}
        title={modalTitle}
        description={modalDescription}
        sx={homeStyle.modalLogin}
      />
      {paymentStep === 1 ? (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Add Payment Method"
          description="Adding a payment method allows you to participate in the auction and make donations."
          isShowCloseButton
          closeButtonTitle="Later"
          mainButtonTitle="Add Payment Method"
          isShowSaveButton
          onSave={handleNextStep}
          mainIcon={<PaymentIcon />}
          sx={profileContactList.modal}
        />
      ) : (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Choose Payment Option"
          isShowCloseButton
          closeButtonTitle="Cancel"
          mainButtonTitle="Confirm"
          isShowSaveButton
          isSaveButtonDisable={isStarting}
          onSave={handleConfirmPayment}
          sx={profileContactList.modal}
        >
          <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
        </Modal>
      )}
    </Box>
  );
};

export default Home;

// const Home = ({slugFromProps}: {slugFromProps?: string}) => {
//   const {slug: slugFromUrl} = useParams();

//   const navigate = useNavigate();
//   const dispatch = useAppDispatch();

//   const slug = slugFromUrl && slugFromUrl !== 'undefined' ? slugFromUrl : slugFromProps;

//   const user = useSelector(getUserSelector);
//   const event = useAppSelector(getCurrentEventSelector);
//   const sponsorCategories = useAppSelector(getSponsorCategoriesSelector);

//   const [isShowAddTicketModal, setIsShowAddTicketModal] = useState<boolean>(false);
//   const [isNeedBeLoggedModalOpen, setIsNeedBeLoggedModalOpen] = useState(false);
//   const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
//   const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
//   const [isSuccessModalOpen2, setIsSuccessModalOpen2] = useState(false);
//   const [isAttentionModalOpen, setIsAttentionModalOpen] = useState(false);
//   const [modalTitle, setModalTitle] = useState('');
//   const [modalDescription, setModalDescription] = useState('');
//   const [isLoading, setIsLoading] = useState(true);
//   const [selectedOption, setSelectedOption] = useState(donationOptions[0]);
//   const [error, setError] = useState('');
//   const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
//   const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
//   const [paymentOption, setPaymentOption] = useState(
//     user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
//   );

//   const isLaptop = useMediaQuery('(min-width:1200px)');

//   const { hasEnded } = useAuctionTimer(event?.endAuctionData);
//   const {isStarting, isWaiting, waitForAttachment, startAttachment} = useCardAttachment(event?.id);

//   const UNCATEGORIZED = 'Uncategorized';

//   const progressValue = event?.isGoalAmount
//     ? event?.goalAmount > (event?.raisedAmount ?? 0)
//       ? ((event?.raisedAmount ?? 0) / event?.goalAmount) * 100
//       : 100
//     : (event?.raisedAmount ?? 0) > 0
//     ? 100
//     : 0;

//   useEffect(() => {
//     if (user?.paymentMethod) setPaymentOption(user.paymentMethod);
//   }, [user?.paymentMethod]);

//   const handleAttentionModalChange = (title: string, description: string) => {
//     if (title && description) {
//       setModalTitle(title);
//       setModalDescription(description);
//     }
//     setIsAttentionModalOpen(!isAttentionModalOpen);
//   };

//   const handleAddTicketModalShow = async () => {
//     if (!user) {
//       setIsNeedBeLoggedModalOpen(true);
//     } else if (!event?.tableInfos?.length) {
//       handleAttentionModalChange(
//         'Table Tiers Coming Soon',
//         'The organizer hasn`t set up the table tiers yet. Please check back later'
//       );
//       // eslint-disable-next-line no-useless-return
//       return;
//     } else {
//       await getTableData();
//       isLaptop ? setIsShowAddTicketModal(true) : navigate(ROUTER_URL.USER_PURCHASE_TICKETS);
//     }
//   };

//   const addPaymentOption = async () => {
//     if (!user?.bidderId || !user?.id) return;
//     try {
//       const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
//       if (res.status === 204) {
//         await dispatch(getUserInfo(user?.id)).unwrap();
//         toast.success('Payment method added successfully!');
//       }

//       handleClosePaymentModal();
//     } catch (error) {
//       toast.error(getErrorMessage(error));
//     }
//   };
//   const handleNextStep = () => setPaymentStep(2);
//   const handleClosePaymentModal = () => {
//     setIsShowPaymentModal(false);
//     setPaymentStep(1);
//   };

//   const handleMakeDonationModalShow = () => {
//     if (!user) {
//       setIsNeedBeLoggedModalOpen(true);
//     } else if (!event?.isFundTheMission) {
//       handleAttentionModalChange(
//         'Thank You for Wanting to Help!',
//         'The organizer has paused donation for this auction.'
//       );
//     } else if (!user.paymentMethod) {
//       setIsShowPaymentModal(true);
//     } else {
//       setIsDonationModalOpen(true);
//       setSelectedOption(donationOptions[0]);
//     }
//   };

//   const groupSponsorsByCategory = (sponsors: EventSponsor[] | null) =>
//     (sponsors || []).reduce<Record<string, EventSponsor[]>>((acc, sponsor) => {
//       const key = sponsor.category?.name.toLowerCase() || UNCATEGORIZED;
//       if (!acc[key]) acc[key] = [];
//       acc[key].push(sponsor);
//       return acc;
//     }, {});

//   const groupedSponsors = groupSponsorsByCategory(event?.sponsors || []);

//   const sortedCategoryNames = useMemo(() => {
//     const knownOrder = new Map((sponsorCategories || []).map((c) => [c.name, c.sortOrder]));
//     return Object.keys(groupedSponsors).sort(
//       (a, b) => (knownOrder.get(a) ?? Infinity) - (knownOrder.get(b) ?? Infinity)
//     );
//   }, [groupedSponsors, sponsorCategories]);

//   const handleAddDonation = async () => {
//     if (!user || !event) return;

//     const numericAmount = parseFloat(selectedOption.replace(',', '.'));

//     if (!selectedOption || Number.isNaN(numericAmount) || numericAmount <= 0) {
//       toast.error('Donation amount should be greater than 0');
//       return;
//     }

//     const reqBody = {
//       amount: numericAmount,
//       eventId: event?.id,
//     };

//     try {
//       await dispatch(userMakeDonation({reqBody, apiInstance: userAPI})).unwrap();
//       await dispatch(getDonationListByEvent({reqBody: event?.id, apiInstance: userAPI})).unwrap();
//       setIsSuccessModalOpen(true);
//       setIsDonationModalOpen(false);
//     } catch (error) {
//       toast.error(getErrorMessage(error));
//     } finally {
//       setError('');
//     }
//   };

//   useEffect(() => {
//     if (selectedOption || Number(selectedOption) !== 0) setError('');
//   }, [selectedOption]);

//   const getAuctionListData = async () => {
//     if (!slug) {
//       setIsLoading(false);
//       return;
//     }

//     try {
//       await dispatch(getEventBySlug(slug)).unwrap();
//     } catch (error) {
//       toast.error(getErrorMessage(error));
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const getTableData = async () => {
//     if (!event?.id) return;

//     try {
//       const reqBody = getDefaultRequestBody(event.id, EVENT_ID);

//       await Promise.all([
//         dispatch(getTicketsList({reqBody, apiInstance: userAPI})).unwrap(),
//         dispatch(getTicketsTiers({reqBody: event.id, apiInstance: userAPI})).unwrap(),
//       ]);
//       dispatch(clearPrivateTicketsTiers());
//     } catch (error) {
//       toast.error(getErrorMessage(error));
//     }
//   };

//   const onSuccess = () => {
//     setIsSuccessModalOpen2(true);
//   };

//   useEffect(() => {
//     setIsLoading(true);
//     getAuctionListData();
//   }, [slug]);

//   if (isLoading) {
//     return (
//       <Box sx={homeStyle.wrapper}>
//         <Loader parentClassName={homeStyle.loader} size={50} />
//       </Box>
//     );
//   }

//   return (
//     <Box sx={homeStyle.wrapper}>
//       <Box sx={homeStyle.imageWrapper}>
//         <Box sx={homeStyle.image}>
//           <img src={event?.image} style={{width: '100%'}} alt="eventImg" />
//         </Box>
//         <Box sx={homeStyle.sectionWrapper}>
//           <Box>
//             <Typography variant={H6} sx={homeStyle.title}>
//               {event?.name}
//             </Typography>
//             <Box sx={{marginBottom: '8px'}}>
//               <Typography variant={BODY2} sx={homeStyle.address}>
//                 <LocationIcon />
//                 {event?.location}
//               </Typography>
//               <Typography variant={BODY2} sx={homeStyle.address}>
//                 <ProfileIcon />
//                 {` Attendees: ${event?.checkedIn} of ${event?.allGuests}`}
//               </Typography>
//             </Box>
//             <Typography variant={BODY2} sx={homeStyle.data}>
//               Start:
//               <Typography sx={homeStyle.time}>
//                 <Typography variant={BODY2} sx={homeStyle.alignWrapper}>
//                   <CalendarIcon />
//                   {dayjs(event?.startData).format(MM_DD_YYYY_SLASH)}
//                 </Typography>
//                 <Typography variant={BODY2} sx={homeStyle.alignWrapper}>
//                   <ClockIcon /> {dayjs(event?.startData).format(MERIDIEM_HOUR_FORMAT)}
//                 </Typography>
//               </Typography>
//             </Typography>
//             <Typography variant={BODY2} sx={homeStyle.data}>
//               End:
//               <Typography sx={homeStyle.time}>
//                 <Typography variant={BODY2} sx={homeStyle.alignWrapper}>
//                   <CalendarIcon />
//                   {dayjs(event?.endData).format(MM_DD_YYYY_SLASH)}
//                 </Typography>
//                 <Typography variant={BODY2} sx={homeStyle.alignWrapper}>
//                   <ClockIcon />
//                   {dayjs(event?.endData).format(MERIDIEM_HOUR_FORMAT)}
//                 </Typography>
//               </Typography>
//             </Typography>
//           </Box>

//           <Box sx={homeStyle.progressWrap}>
//             <Box sx={homeStyle.progressValue}>
//               <Typography variant={BODY1} sx={homeStyle.progressTargetAmount}>
//                 <Typography sx={homeStyle.progressTargetGoal} variant={CAPTION}>
//                   Raised
//                 </Typography>
//                 {`${currencyFormatter.format(event?.raisedAmount || 0)}`}
//               </Typography>
//               {event?.isGoalAmount && (
//                 <Typography sx={homeStyle.progressTargetGoal} variant={SUBTITLE2}>
//                   <Typography sx={homeStyle.progressTargetGoal} variant={CAPTION}>
//                     Goal amount
//                   </Typography>
//                   {`${currencyFormatter.format(event?.goalAmount)}`}
//                 </Typography>
//               )}
//             </Box>
//             <ProgressBar value={progressValue} />
//           </Box>

//           <Box sx={homeStyle.buttonWrapper}>
//             <Button
//               startIcon={
//                 <Box sx={homeStyle.moneyIconWrapper}>
//                   <MoneyIcon />
//                 </Box>
//               }
//               variant={OUTLINE}
//               title="Make Donation"
//               onClick={handleMakeDonationModalShow}
//               disableRipple
//             />
//             <Button
//               startIcon={
//                 <Box sx={homeStyle.iconWrapper}>
//                   <TicketIcon />
//                 </Box>
//               }
//               title="Buy Tickets"
//               onClick={handleAddTicketModalShow}
//               disableRipple
//             />
//           </Box>
//         </Box>
//       </Box>
//       {event?.description && (
//         <Box sx={homeStyle.textWrapper}>
//           <Typography variant={SUBTITLE1} sx={homeStyle.title}>
//             About The Event
//           </Typography>
//           <Typography sx={homeStyle.description} variant={BODY1}>
//             {event?.description}
//           </Typography>
//         </Box>
//       )}

//       {!!event?.sponsors?.length && (
//         <Box sx={homeStyle.textWrapper}>
//           <Typography variant={SUBTITLE1} sx={homeStyle.title}>
//             Event Sponsors
//           </Typography>
//           {sortedCategoryNames.map((categoryName) => (
//             <Box key={categoryName}>
//               <Typography variant={SUBTITLE2} sx={homeStyle.sponsorTitle}>
//                 {categoryName}
//               </Typography>
//               <Box sx={homeStyle.sponsorList}>
//                 {groupedSponsors[categoryName].map((item: EventSponsor) => (
//                   <Box key={item.id} sx={homeStyle.sponsor}>
//                     <Box sx={homeStyle.sponsorImage}>
//                       <img src={getImagePreview(item.logo || TestImage)} alt={item.name || IMAGE_PREVIEW_ALT} />
//                     </Box>
//                     <Box sx={homeStyle.sponsorName} title={item.name}>
//                       {item.name}
//                     </Box>
//                   </Box>
//                 ))}
//               </Box>
//             </Box>
//           ))}
//         </Box>
//       )}

//       {event?.popularItems && event?.popularItems?.length > 0 && (
//         <Box>
//           <Box sx={homeStyle.popularWrapper}>
//             <Typography variant={SUBTITLE1}>Popular Auction Items</Typography>
//             <Button
//               title="View All Items"
//               endIcon={<ArrowRightIcon />}
//               variant={TEXT}
//               onClick={() => navigate(ROUTER_URL.USER_ALL_ITEMS)}
//             />
//           </Box>
//           <Box sx={homeStyle.lotList}>
//             {event?.popularItems &&
//               event?.popularItems.map((item) => {
//                 return (
//                   <LotItem
//                     key={item.id}
//                     data={item}
//                     auctionType="AllItems"
//                     getListData={getAuctionListData}
//                     hasEnded={hasEnded}
//                   />
//                 );
//               })}
//           </Box>
//         </Box>
//       )}
//       <Modal
//         onClose={() => setIsShowAddTicketModal(false)}
//         open={isShowAddTicketModal}
//         sx={homeStyle.modalTickets}
//         title="Buy Tickets"
//       >
//         <AddTableTicket onCloseModal={() => setIsShowAddTicketModal(false)} onSuccess={onSuccess} />
//       </Modal>
//       <Modal
//         onClose={() => setIsNeedBeLoggedModalOpen(false)}
//         open={isNeedBeLoggedModalOpen}
//         sx={homeStyle.modalLogin}
//         mainIcon={<DangerIcon />}
//       >
//         <NeedBeLoggedModal handleClose={() => setIsNeedBeLoggedModalOpen(false)} />
//       </Modal>
//       <Modal
//         isShowSaveButton
//         mainButtonTitle={`Donate $${selectedOption}`}
//         isSaveButtonDisable={!selectedOption}
//         isShowCloseButton
//         closeButtonTitle="Close"
//         onClose={() => {
//           setIsDonationModalOpen(false);
//           setError('');
//         }}
//         open={isDonationModalOpen}
//         sx={homeStyle.modalBid}
//         onSave={handleAddDonation}
//       >
//         <MakeDonationModal selectedOption={selectedOption} setSelectedOption={setSelectedOption} error={error} />
//       </Modal>
//       <Modal
//         open={isSuccessModalOpen}
//         onClose={() => {
//           setIsSuccessModalOpen(false);
//           setSelectedOption(donationOptions[0]);
//         }}
//         title="Thank You for Your Donation"
//         description={`Your donation of $${selectedOption} successfully processed.`}
//         sx={homeStyle.modalLogin}
//         closeButtonTitle="Done"
//         mainIcon={<SuccessIcon />}
//       />
//       <Modal
//         open={isSuccessModalOpen2}
//         onClose={() => setIsSuccessModalOpen2(false)}
//         title="Thank You For Your Support!"
//         description="Your tickets have been successfully purchased. A receipt and additional information will be delivered by email."
//         sx={homeStyle.modalLogin}
//         mainButtonTitle="Done"
//         onSave={() => setIsSuccessModalOpen2(false)}
//         isShowSaveButton
//         mainIcon={<SuccessIcon />}
//       />
//       <Modal
//         open={isAttentionModalOpen}
//         onClose={() => handleAttentionModalChange('', '')}
//         title={modalTitle}
//         description={modalDescription}
//         sx={homeStyle.modalLogin}
//       />
//       {paymentStep === 1 ? (
//         <Modal
//           open={isShowPaymentModal}
//           onClose={handleClosePaymentModal}
//           title="Add Payment Method"
//           description="Adding a payment method allows you to participate in the auction and make donations."
//           isShowCloseButton
//           closeButtonTitle="Later"
//           mainButtonTitle="Add Payment Method"
//           isShowSaveButton
//           onSave={handleNextStep}
//           mainIcon={<PaymentIcon />}
//           sx={profileContactList.modal}
//         />
//       ) : (
//         <Modal
//           open={isShowPaymentModal}
//           onClose={handleClosePaymentModal}
//           title="Choose Payment Option"
//           isShowCloseButton
//           closeButtonTitle="Cancel"
//           mainButtonTitle="Confirm"
//           isShowSaveButton
//           onSave={addPaymentOption}
//           sx={profileContactList.modal}
//         >
//           <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
//         </Modal>
//       )}
//     </Box>
//   );
// };

// export default Home;
